import {
    FileText,
    BookOpen,
    GitCommitVertical,
    LineChart,
    Brackets,
    BarChart3,
    TrendingUp,
    Layers3,
    Activity,
    CheckCheck,
} from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import {
    Container,
    Section,
    PageHero,
    Eyebrow,
    CardImage,
} from "@/components/Primitives";

const GLOSSARY = [
    {
        term: "VWAP",
        icon: LineChart,
        def: "Volume-weighted average price. The day's average price weighted by volume — a key reference for whether buyers or sellers are in control.",
    },
    {
        term: "Opening range",
        icon: Brackets,
        def: "The high and low set in the first minutes after the open. A structural boundary the engine trades acceptance of, or rejection from.",
    },
    {
        term: "Relative volume (RVOL)",
        icon: BarChart3,
        def: "Today's volume measured against a name's own recent baseline. High RVOL means unusual participation — real interest, not noise.",
    },
    {
        term: "Gap-and-go",
        icon: TrendingUp,
        def: "A stock that gaps on a catalyst and continues in the gap's direction. The Aegis Line's stock engine hunts these in the first hour.",
    },
    {
        term: "Conviction tiers",
        icon: Layers3,
        def: "A defined scale the model uses to grade a setup. Higher conviction permits larger size — within hard caps.",
    },
    {
        term: "ATR-proxy stops",
        icon: Activity,
        def: "Stops sized from an instrument's own recent range, with a minimum floor, so risk stays proportional to volatility.",
    },
    {
        term: "Acceptance",
        icon: CheckCheck,
        def: "Price trading and holding inside a level or range rather than rejecting it — evidence the market agrees on value there.",
    },
];

const CHANGELOG = [
    {
        version: "v0.4.0",
        date: "2026-06-10",
        note: "Decision log gains per-candle reassessment trail. Every hold/scale/exit now stores the structural facts that drove it.",
    },
    {
        version: "v0.3.2",
        date: "2026-05-22",
        note: "Tightened the brief-builder to strip residual setup-score language before the model reads it. The desk stays out of the trader's room.",
    },
    {
        version: "v0.3.0",
        date: "2026-05-04",
        note: "Added the dumb-baseline comparison harness to the evaluation loop. A change now has to beat \u201center every screened candidate\u201d to count.",
    },
    {
        version: "v0.2.1",
        date: "2026-04-18",
        note: "Volatility-aware stop floors recalibrated for thin, low-float names where spreads run wide.",
    },
];

const FIELD_NOTES = [
    "The line we hold against ourselves",
    "Paper P&L is an upper bound",
    "Code is the analyst, the model is the trader",
    "What it actually means to beat a baseline",
    "Why the desk never enters the trader's room",
];

const Card = ({ icon: Icon, title, image, children }) => (
    <div className="group h-full overflow-hidden rounded-lg border border-haze bg-ink-soft transition-colors hover:border-rule/40">
        {image && (
            <CardImage
                src={image}
                alt={title}
                className="h-40 w-full border-b border-haze"
            />
        )}
        <div className="p-7">
            <span className="flex h-11 w-11 items-center justify-center rounded border border-haze text-rule">
                <Icon size={20} />
            </span>
            <h3 className="mt-5 text-lg font-semibold text-text-hi">{title}</h3>
            <div className="mt-3 text-sm leading-relaxed text-text-lo">
                {children}
            </div>
        </div>
    </div>
);

export default function Resources() {
    return (
        <>
            <Seo
                title="Resources"
                description="Docs, a plain-English glossary, and a candid changelog. Understand the The Aegis Line pipeline in depth, no assumed knowledge."
            />
            <PageHero
                eyebrow="Docs, glossary, changelog, field notes"
                title="Read the system, not just the pitch."
                sub="Material that doubles as documentation and proof the system has a readable history."
            />

            <Section>
                <Container>
                    <div className="grid gap-5 md:grid-cols-2">
                        <Reveal>
                            <Card icon={FileText} title="Docs" image="/images/cards/docs.png">
                                How the pipeline works in depth, for readers who
                                want to understand the architecture — screening,
                                the deterministic signal stack, the brief
                                builder, the decision layer, and trade
                                management.
                            </Card>
                        </Reveal>
                        <Reveal delay={0.08}>
                            <Card
                                icon={GitCommitVertical}
                                title="Changelog"
                                image="/images/cards/changelog.png"
                            >
                                Versioned, candid notes on what changed and why.
                                The existence of a changelog signals that the
                                system has a readable history.
                            </Card>
                        </Reveal>
                    </div>

                    {/* Glossary */}
                    <Reveal>
                        <div className="mt-16">
                            <Eyebrow className="mb-5 flex items-center gap-2">
                                <BookOpen size={14} /> Glossary
                            </Eyebrow>
                            <h2 className="text-2xl font-semibold text-text-hi sm:text-3xl">
                                Defined plainly, no assumed knowledge.
                            </h2>
                            <dl className="mt-8 divide-y divide-haze rounded-lg border border-haze">
                                {GLOSSARY.map((g) => (
                                    <div
                                        key={g.term}
                                        className="grid gap-2 p-5 transition-colors hover:bg-ink/40 sm:grid-cols-[220px_1fr] sm:gap-6"
                                    >
                                        <dt className="flex items-center gap-3">
                                            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded border border-haze bg-ink text-rule">
                                                <g.icon size={15} />
                                            </span>
                                            <span className="font-mono text-sm text-rule">
                                                {g.term}
                                            </span>
                                        </dt>
                                        <dd className="text-sm leading-relaxed text-text-lo">
                                            {g.def}
                                        </dd>
                                    </div>
                                ))}
                            </dl>
                        </div>
                    </Reveal>

                    {/* Changelog */}
                    <Reveal>
                        <div className="mt-16">
                            <Eyebrow className="mb-5 flex items-center gap-2">
                                <GitCommitVertical size={14} /> Changelog
                            </Eyebrow>
                            <h2 className="text-2xl font-semibold text-text-hi sm:text-3xl">
                                What changed, and why.
                            </h2>
                            <ol className="mt-8 space-y-4 border-l border-haze pl-6">
                                {CHANGELOG.map((c) => (
                                    <li key={c.version} className="relative">
                                        <span className="absolute -left-[31px] top-1.5 h-2.5 w-2.5 rounded-full border border-rule bg-ink" />
                                        <div className="flex flex-wrap items-center gap-3">
                                            <span className="font-mono text-sm font-semibold text-text-hi">
                                                {c.version}
                                            </span>
                                            <span className="font-mono text-xs text-text-lo">
                                                {c.date}
                                            </span>
                                        </div>
                                        <p className="mt-1.5 text-sm leading-relaxed text-text-lo">
                                            {c.note}
                                        </p>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    </Reveal>
                    {/* Field notes */}
                    <Reveal>
                        <div className="mt-16">
                            <Eyebrow className="mb-5 flex items-center gap-2">
                                <FileText size={14} /> Field notes
                            </Eyebrow>
                            <h2 className="text-2xl font-semibold text-text-hi sm:text-3xl">
                                Essays on the methodology.
                            </h2>
                            <ul className="mt-8 divide-y divide-haze rounded-lg border border-haze">
                                {FIELD_NOTES.map((title) => (
                                    <li
                                        key={title}
                                        className="flex items-center justify-between gap-4 p-5 transition-colors hover:bg-ink/40"
                                    >
                                        <span className="text-sm leading-relaxed text-text-hi sm:text-base">
                                            “{title}”
                                        </span>
                                        <span className="shrink-0 font-mono text-[10px] uppercase tracking-wider text-text-lo">
                                            Essay
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </Reveal>
                </Container>
            </Section>
        </>
    );
}
